import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import api from "../lib/api";
import { creditsApi } from "../lib/api";
import { Check, Sparkles, Zap, Crown, Coins, FileText, Linkedin, Facebook, Instagram, ImagePlus, Clock } from "lucide-react";

const planIcons = { basic: Sparkles, standard: Zap, pro: Zap, business: Crown };
const planColors = { basic: "#00D4FF", standard: "#10B981", pro: "#F0A050", business: "#A78BFA" };

// Launch offer countdown — end date ISO (UTC). Edit this value to extend/shorten the offer.
// Reset 24 abr 2026 + 20 días = 14 may 2026
const OFFER_END_ISO = "2026-05-14T23:59:59Z";

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(targetIso);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { diff, days, hours, minutes, seconds, expired: diff === 0 };
}

const actionLabels = {
  article_blog: { label: "Artículo Blog", icon: FileText },
  post_linkedin: { label: "Post LinkedIn", icon: Linkedin },
  post_facebook: { label: "Post Facebook", icon: Facebook },
  post_instagram: { label: "Post Instagram", icon: Instagram },
  image_ai: { label: "Imagen IA", icon: ImagePlus },
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [costs, setCosts] = useState({});
  const [loading, setLoading] = useState(null);
  const countdown = useCountdown(OFFER_END_ISO);
  const hasActiveOffer = plans.some(p => p.offer_enabled && p.offer_price) && !countdown.expired;

  useEffect(() => {
    Promise.all([
      api.get("/plans").then(r => setPlans(r.data)),
      api.get("/packs").then(r => setPacks(r.data)),
      creditsApi.config().then(r => setCosts(r.data.costs || {}))
    ]).catch(() => {});
  }, []);

  const handleCheckout = async (planId, packId) => {
    const key = planId || packId;
    setLoading(key);
    try {
      let res;
      if (planId) {
        // Plans use subscription
        res = await api.post("/subscribe", {
          plan_id: planId,
          origin_url: window.location.origin,
        });
      } else {
        // Packs use one-time checkout
        res = await api.post("/checkout", {
          pack_id: packId,
          origin_url: window.location.origin,
        });
      }
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(null); }
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 animate-fade-in-up" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
            Planes y Créditos
          </h1>
          <p className="text-base animate-fade-in-up animate-fade-in-up-delay-1" style={{ color: "#94A3B8" }}>
            Cada acción consume créditos. Elige un plan o compra créditos sueltos.
          </p>
          {user && (
            <div className="mt-4 flex items-center justify-center gap-3 animate-fade-in-up animate-fade-in-up-delay-2">
              <Badge className="text-sm px-4 py-1.5 rounded-full" style={{ background: "rgba(0,212,255,0.1)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.2)" }}>
                <Coins className="w-4 h-4 mr-1.5" />{user.credits || 0} créditos
              </Badge>
              <Badge className="text-sm px-3 py-1.5 rounded-full capitalize" style={{ background: "rgba(240,160,80,0.1)", color: "#F0A050" }}>
                Plan: {user.plan || "free"}
              </Badge>
            </div>
          )}
        </div>

        {/* Launch Offer Countdown */}
        {hasActiveOffer && (
          <div data-testid="offer-countdown" className="mb-8 rounded-2xl border p-5 animate-fade-in-up animate-fade-in-up-delay-2"
            style={{
              background: "linear-gradient(135deg, rgba(74,222,128,0.08), rgba(0,212,255,0.06))",
              borderColor: "rgba(74,222,128,0.3)",
            }}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: "#4ADE80" }} />
                <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: "#4ADE80", fontFamily: "'Space Grotesk'" }}>
                  Oferta de lanzamiento termina en
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3" data-testid="countdown-timer">
                {[
                  { v: countdown.days, l: "días" },
                  { v: countdown.hours, l: "h" },
                  { v: countdown.minutes, l: "m" },
                  { v: countdown.seconds, l: "s" },
                ].map((u, i) => (
                  <div key={i} className="flex flex-col items-center min-w-[52px] px-2 py-1.5 rounded-lg"
                    style={{ background: "rgba(11,22,34,0.6)", border: "1px solid rgba(74,222,128,0.2)" }}>
                    <span className="text-xl sm:text-2xl font-bold tabular-nums leading-none"
                      style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                      {String(u.v).padStart(2, "0")}
                    </span>
                    <span className="text-[10px] mt-0.5 uppercase tracking-wider" style={{ color: "#94A3B8" }}>
                      {u.l}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Credit costs table */}
        <div className="rounded-xl border p-6 mb-8 animate-fade-in-up animate-fade-in-up-delay-2" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
          <h3 className="text-sm font-medium mb-4 flex items-center gap-2" style={{ color: "#E2E8F0" }}>
            <Coins className="w-4 h-4" style={{ color: "#F0A050" }} />Costo por acción (en créditos)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(costs).map(([key, cost]) => {
              const info = actionLabels[key] || { label: key, icon: FileText };
              const Icon = info.icon;
              return (
                <div key={key} className="rounded-lg p-3 text-center" style={{ background: "#0B1622" }}>
                  <Icon className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#00D4FF" }} />
                  <div className="text-xs mb-1" style={{ color: "#94A3B8" }}>{info.label}</div>
                  <div className="text-lg font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{cost}</div>
                  <div className="text-xs" style={{ color: "#94A3B8" }}>créditos</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan, i) => {
            const Icon = planIcons[plan.id] || Sparkles;
            const color = planColors[plan.id] || "#00D4FF";
            const isPopular = plan.id === "pro";
            const isCurrent = user?.plan === plan.id;
            return (
              <div key={plan.id} data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-2xl p-6 border transition-all duration-300 animate-fade-in-up animate-fade-in-up-delay-${i + 1}`}
                style={{ background: isPopular ? "linear-gradient(180deg, #0F2035 0%, #152D4A 100%)" : "#0F2035",
                  borderColor: isPopular ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                  boxShadow: isPopular ? "0 0 40px rgba(0,212,255,0.08)" : "none" }}>
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="text-xs px-3 py-1 rounded-full" style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>Popular</Badge>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                  <Icon className="w-5 h-5" style={{ color }} />
                  <span className="text-lg font-semibold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{plan.name}</span>
                </div>
                {plan.offer_enabled && plan.offer_price ? (
                  <div className="mb-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-4xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                        {plan.offer_price}€
                      </span>
                      <span className="text-lg line-through" style={{ color: "#64748B" }}>
                        {plan.price}€
                      </span>
                      <span className="text-sm" style={{ color: "#94A3B8" }}>/mes</span>
                    </div>
                    {plan.offer_text && (
                      <div className="mt-1 inline-block text-xs px-2 py-0.5 rounded-full"
                        data-testid={`offer-badge-${plan.id}`}
                        style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
                        {plan.offer_text}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-2">
                    <span className="text-4xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{plan.price}€</span>
                    <span className="text-sm ml-1" style={{ color: "#94A3B8" }}>/mes</span>
                  </div>
                )}
                <div className="mb-6 text-sm font-medium" style={{ color }}>
                  {plan.credits} créditos incluidos
                </div>
                <div className="space-y-3 mb-8">
                  {(Array.isArray(plan.features) ? plan.features : []).map((f, fi) => (
                    <div key={fi} className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color }} />
                      <span className="text-sm" style={{ color: "#CBD5E1" }}>{f}</span>
                    </div>
                  ))}
                  {plan.includes && (
                    <div className="pt-2 mt-2 text-xs italic" style={{ color: "#94A3B8", borderTop: "1px solid rgba(30,58,95,0.5)" }}>
                      {plan.includes}
                    </div>
                  )}
                </div>
                <Button data-testid={`subscribe-${plan.id}`} onClick={() => handleCheckout(plan.id, null)}
                  disabled={loading === plan.id || isCurrent}
                  className="w-full rounded-full h-11 text-sm font-medium"
                  style={{ background: isCurrent ? "rgba(148,163,184,0.1)" : isPopular ? "linear-gradient(135deg, #00B4D8, #00D4FF)" : `${color}22`,
                    color: isCurrent ? "#94A3B8" : isPopular ? "#0B1622" : color }}>
                  {loading === plan.id ? "Procesando..." : isCurrent ? "Plan actual" : "Suscribirse"}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Credit Packs */}
        <Separator className="my-8" style={{ background: "#1E3A5F" }} />
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>Créditos Sueltos</h2>
          <p className="text-sm" style={{ color: "#94A3B8" }}>Compra créditos sin suscripción mensual</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {packs.map((pack) => (
            <div key={pack.id} data-testid={`pack-${pack.id}`}
              className="rounded-xl border p-5 text-center transition-all duration-200 card-glow" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <Coins className="w-8 h-8 mx-auto mb-2" style={{ color: "#F0A050" }} />
              <div className="text-2xl font-bold mb-1" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{pack.credits}</div>
              <div className="text-xs mb-3" style={{ color: "#94A3B8" }}>créditos</div>
              <div className="text-xl font-bold mb-4" style={{ color: "#4ADE80", fontFamily: "'Space Grotesk'" }}>{pack.price}€</div>
              <Button data-testid={`buy-pack-${pack.id}`} onClick={() => handleCheckout(null, pack.id)}
                disabled={loading === pack.id} size="sm"
                className="w-full rounded-full text-sm" style={{ background: "rgba(240,160,80,0.15)", color: "#F0A050" }}>
                {loading === pack.id ? "..." : "Comprar"}
              </Button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
